'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { ORDER_STATUS } from 'my-purchases/constants/orderStatus'
import { getStatusLabels } from 'my-purchases/utils/getStatusLabels'
import { useMemo } from 'react'
import { cx } from 'utils/cx'
import { dateFormatter } from 'utils/dateFormatter/dateFormatter'

import styles from './OrderTrackerStep.module.scss'

interface OrderTrackerStepProps {
	readonly orderStatusIdx: number
	readonly stepStatus: ORDER_STATUS
	readonly stepStatusIdx: number
	readonly eventDate?: string
}

export function OrderTrackerStep({
	orderStatusIdx,
	stepStatus,
	stepStatusIdx,
	eventDate,
}: OrderTrackerStepProps) {
	const { t } = useLabels()
	const { country } = useMasterData()
	const { locale } = country

	const isPreviousStep = orderStatusIdx - 1 === stepStatusIdx
	const isCurrentStep = orderStatusIdx === stepStatusIdx
	const isNextStep = orderStatusIdx < stepStatusIdx
	const isStatusDone = orderStatusIdx > stepStatusIdx
	const { detailsLabel } = getStatusLabels({ status: stepStatus })

	const [weekday, month, hours] = dateFormatter({
		dateString: eventDate ?? '',
		locale,
	}).split(',')

	const trackDate = {
		weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
		month,
		hours,
	}

	const ariaLabelStep = useMemo(() => {
		if (isPreviousStep) {
			return t('accessibility.myPurchases.status.previous.text')
		}

		if (isCurrentStep) {
			return t('accessibility.myPurchases.status.active.text')
		}

		if (isNextStep) {
			return t('accessibility.myPurchases.status.next.text')
		}

		return null
	}, [isPreviousStep, isCurrentStep, isNextStep])

	return (
		<li
			data-testid={detailsLabel}
			className={cx(
				styles.step,
				isStatusDone && styles.done,
				isCurrentStep && styles.current,
				eventDate && styles.eventDate
			)}
			{...(ariaLabelStep && {
				'aria-label': `${ariaLabelStep} ${t(detailsLabel)}`,
			})}
		>
			{t(detailsLabel)}
			{eventDate && (
				<p className={styles.trackDate}>
					<span className={styles.date}>
						{trackDate?.weekday}, {trackDate?.month}
					</span>
					<span className={styles.date}>{trackDate?.hours}</span>
				</p>
			)}
		</li>
	)
}
